import { Model } from '..'
import { PagedCollection } from '..'
import Enhancer from '../Enhancer'
import Vue from 'vue'
import mixins from '@/modules/mixins/global'
import store from '@/modules/store'
import MarketingCookie from '@/api/client/MarketingCookie'

class Address extends Model {
    constructor(json, ...args) {
        super(...args)
        this.apiBase = 'user-addresses'
        this._model = json || {}
        return new Proxy(this, Enhancer)
    }
    get link() {
        return this._model.id ? `/api/v1/user-addresses/${this._model.id}` : null
    }

    roundCoordinates(value) {
        if (typeof value === 'number' || typeof value === 'string') {
            const num = typeof value === 'string' ? parseFloat(value) : value
            if (!isNaN(num)) {
                return Math.round(num * 1000000) / 1000000
            }
        }
        return value
    }

    save(_url, _model) {
        const modelToSave = typeof _model !== 'undefined' ? _model : this.model
        if (modelToSave.latitude !== undefined) {
            modelToSave.latitude = this.roundCoordinates(modelToSave.latitude)
        }
        if (modelToSave.longitude !== undefined) {
            modelToSave.longitude = this.roundCoordinates(modelToSave.longitude)
        }
        if (modelToSave.original_latitude !== undefined) {
            modelToSave.original_latitude = this.roundCoordinates(modelToSave.original_latitude)
        }
        if (modelToSave.original_longitude !== undefined) {
            modelToSave.original_longitude = this.roundCoordinates(modelToSave.original_longitude)
        }
        return super.save(_url, modelToSave).then((res) => {
            MarketingCookie.sync()
            return res
        })
    }

    destroy() {
        return super.destroy().then((res) => {
            MarketingCookie.sync()
            return res
        })
    }
}

class Addresses extends PagedCollection {
    #blockInit = false
    constructor(...args) {
        super(...args)
        this.apiBase = 'user-addresses'
    }

    push(item) {
        super.push(new Address(item))
    }

    findAddress({ id, place_id, latitude, longitude, original_latitude, original_longitude }) {
        if (
            !id &&
            !place_id &&
            !latitude &&
            !longitude &&
            !original_latitude &&
            !original_longitude
        )
            return null
        let address

        // search by id
        if (id) {
            address = this.items.find((item) => item.id === id)
            if (address) return address
        }

        // search by place_id
        if (place_id) {
            address = this.items.find((item) => item.place_id === place_id)
            if (address) return address
        }

        if (!latitude && !longitude && !original_latitude && !original_longitude) return null

        // search by original_latitude & original_longitude
        if (original_latitude && original_longitude) {
            original_latitude = this.toNumber(original_latitude)
            original_longitude = this.toNumber(original_longitude)
            address = this.items.find(
                (item) =>
                    item.original_latitude === original_latitude &&
                    item.original_longitude === original_longitude
            )
            if (address) return address
        }

        if (!latitude && !longitude) return null

        // search by latitude & longitude
        latitude = this.toNumber(latitude)
        longitude = this.toNumber(longitude)
        return this.items.find((item) => item.latitude === latitude && item.longitude === longitude)
    }

    isValidLocation(location) {
        return (
            location &&
            location.latitude &&
            location.longitude &&
            !isNaN(location.latitude) &&
            !isNaN(location.longitude) &&
            -90 <= location.latitude &&
            location.latitude <= 90 &&
            -180 <= location.longitude &&
            location.longitude <= 180
        )
    }
    isSameLocation(location1, location2) {
        if (!this.isValidLocation(location1) || !this.isValidLocation(location2)) return false
        location1.latitude = this.toNumber(location1.latitude)
        location1.longitude = this.toNumber(location1.longitude)
        return (
            location1.latitude === location2.latitude && location1.longitude === location2.longitude
        )
    }
    toNumber(value) {
        if (typeof value === 'string') value = Number(value)
        return value
    }
    async initAddresses() {
        if (!this.total && Vue.prototype.$user?.isLoggedIn && !this.#blockInit) {
            this.#blockInit = true
            await this.fetch()
            const address = this.findItem(mixins.getId(Vue.prototype.$cart.model.address))
            if (address && !Vue.prototype.$cart.isAddressEqual(address.model)) {
                if (store.getters['active/canteen']?.id) Vue.prototype.$cart = address
                else await store.dispatch('order/setAddress', address.model)
            }
            this.#blockInit = false
        }
        return
    }

    async fetch(restaurantId) {
        await super.fetch({
            restaurant: restaurantId,
            page: undefined,
            items_per_page: undefined,
            limit: undefined,
        })
        const sorted = this.items.sort((a, b) => {
            return a.position > b.position ? 1 : -1
        })
        MarketingCookie.sync()
        return sorted
    }
}

export { Address, Addresses }
